import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FaPlus, FaEdit, FaTrash } from 'react-icons/fa';
import Modal from '../../components/shared/Modal';
import Input from '../../components/shared/Input';
import TextArea from '../../components/shared/TextArea';
import Checkbox from '../../components/shared/Checkbox';
import { ActionButton } from '../../components/styled/CommonStyles';
import { formatCurrency } from '../../utils/utils';
import ServiceModal, { getUnitInfo } from '../estimates/ServiceModal';
import { createChangeOrder, updateChangeOrder } from './changeOrderService';
import DiscountInput from '../../components/shared/DiscountInput';
import { calcDiscount, discountPayload, discountFormValue } from '../../utils/discount';

const FormWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${(props) => props.theme.spacing(2)};
`;

const ItemList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${(props) => props.theme.spacing(1)};
  margin-top: ${(props) => props.theme.spacing(1)};
`;

const ItemRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${(props) => props.theme.spacing(2)};
  background: ${(props) => props.theme.colors.background};
  border-radius: ${(props) => props.theme.borderRadius};
  gap: ${(props) => props.theme.spacing(2)};

  .item-info {
    flex: 1;
    min-width: 0;
  }

  .item-name {
    font-weight: ${(props) => props.theme.typography.fontWeight.medium};
  }

  .item-meta {
    font-size: 12px;
    color: ${(props) => props.theme.colors.textSecondary};
  }

  .item-actions {
    display: flex;
    gap: 6px;
  }
`;

const TotalsRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: ${(props) => props.theme.spacing(2)} 0;
  font-size: 1.1em;
  font-weight: bold;
  border-top: 1px solid ${(props) => props.theme.colors.inputGray}44;
  margin-top: ${(props) => props.theme.spacing(1)};
`;

const AddItemButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: ${(props) => props.theme.spacing(1.5)};
  border: 1px dashed ${(props) => props.theme.colors.inputGray};
  background: transparent;
  color: ${(props) => props.theme.colors.textSecondary};
  border-radius: ${(props) => props.theme.borderRadius};
  cursor: pointer;

  &:hover {
    color: ${(props) => props.theme.colors.primary};
    border-color: ${(props) => props.theme.colors.primary};
  }
`;

const KindToggle = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${(props) => props.theme.spacing(1)};
`;

const KindOption = styled.button`
  padding: ${(props) => props.theme.spacing(1.5)};
  border-radius: ${(props) => props.theme.borderRadius};
  border: 2px solid ${(props) => (props.$active ? props.theme.colors.primary : `${props.theme.colors.inputGray}66`)};
  background: ${(props) => (props.$active ? `${props.theme.colors.primary}14` : 'transparent')};
  color: ${(props) => props.theme.colors.textPrimary};
  cursor: pointer;
  text-align: left;

  strong { display: block; }
  span {
    font-size: 12px;
    color: ${(props) => props.theme.colors.textSecondary};
  }
`;

const QuickPick = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: ${(props) => props.theme.spacing(1)};

  button {
    padding: 4px 10px;
    border-radius: 14px;
    border: 1px solid ${(props) => props.theme.colors.inputGray};
    background: transparent;
    color: ${(props) => props.theme.colors.textPrimary};
    font-size: 12px;
    cursor: pointer;
  }
  button:hover {
    border-color: ${(props) => props.theme.colors.primary};
    color: ${(props) => props.theme.colors.primary};
  }
`;

// Line amounts are kept positive in the form for both kinds; the server stores
// Credit lines negative. Credits loaded for editing arrive negative, hence abs.
const normalizeItem = (item) => ({
  serviceId: item.serviceId || item.ServiceId || item.Service?.id,
  serviceName: item.serviceName || item.Service?.service_name || '',
  pricePerSqft: parseFloat(item.pricePerSqft) || 0,
  totalArea: parseFloat(item.totalArea) || 0,
  totalPrice: Math.abs(parseFloat(item.totalPrice) || 0),
  measurementUnit: item.measurementUnit || 'sqft',
  observation: item.observation || '',
  areaMeasurements: item.areaMeasurements || [],
});

const ChangeOrderFormModal = ({ isOpen, onClose, onSaved, estimateId, estimate, services, existing }) => {
  const [kind, setKind] = useState('Addition');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [reason, setReason] = useState('');
  const [requiresSignature, setRequiresSignature] = useState(true);
  const [discountType, setDiscountType] = useState('percentage'); // 'percentage' | 'fixed'
  const [discountValue, setDiscountValue] = useState('');
  const [discountReason, setDiscountReason] = useState('');
  const [items, setItems] = useState([]);
  const [serviceModalOpen, setServiceModalOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    if (existing) {
      setKind(existing.kind || 'Addition');
      setTitle(existing.title || '');
      setDescription(existing.description || '');
      setReason(existing.reason || '');
      setRequiresSignature(existing.requiresSignature !== false);
      const savedDiscount = discountFormValue(existing);
      setDiscountType(savedDiscount.type);
      setDiscountValue(savedDiscount.value);
      setDiscountReason(existing.discountReason || '');
      setItems((existing.items || []).map(normalizeItem));
    } else {
      setKind('Addition');
      setTitle('');
      setDescription('');
      setReason('');
      setRequiresSignature(true);
      setDiscountType('percentage');
      setDiscountValue('');
      setDiscountReason('');
      setItems([]);
    }
    setError('');
  }, [isOpen, existing]);

  const isCredit = kind === 'Credit';
  const sign = isCredit ? -1 : 1;
  const subtotal = items.reduce((sum, it) => sum + (parseFloat(it.totalPrice) || 0), 0);
  // Credits never carry a discount (it would shrink the credit)
  const discountCalc = isCredit ? calcDiscount(subtotal, 'percentage', '') : calcDiscount(subtotal, discountType, discountValue);
  const discountAmount = discountCalc.amount;
  const total = discountCalc.total;

  // Credit a whole service from the original quote in one click; edit the line
  // afterwards for a partial credit.
  const addOriginalService = (estItem) => {
    setItems([...items, normalizeItem({
      serviceId: estItem.serviceId || estItem.Service?.id,
      serviceName: estItem.Service?.service_name,
      pricePerSqft: estItem.pricePerSqft,
      totalArea: estItem.totalArea,
      totalPrice: estItem.totalPrice,
      measurementUnit: estItem.measurementUnit,
    })]);
  };

  const openAddItem = () => {
    setEditingIndex(null);
    setServiceModalOpen(true);
  };

  const openEditItem = (index) => {
    setEditingIndex(index);
    setServiceModalOpen(true);
  };

  const handleItemSave = (serviceData) => {
    const next = [...items];
    const normalized = normalizeItem(serviceData);
    if (editingIndex !== null) {
      next[editingIndex] = { ...next[editingIndex], ...normalized };
    } else {
      next.push(normalized);
    }
    setItems(next);
    setServiceModalOpen(false);
    setEditingIndex(null);
  };

  const handleItemRemove = (index) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (!title.trim()) {
      setError('Title is required');
      return;
    }
    if (items.length === 0) {
      setError('Add at least one item');
      return;
    }
    if (!isCredit && discountCalc.error) {
      setError(discountCalc.error);
      return;
    }

    const payload = {
      estimateId,
      kind,
      title: title.trim(),
      description,
      reason,
      requiresSignature,
      ...(isCredit ? discountPayload('percentage', '') : discountPayload(discountType, discountValue)),
      discountReason: !isCredit && discountAmount > 0 && discountReason.trim() ? discountReason.trim() : null,
      items: items.map((it) => ({
        serviceId: parseInt(it.serviceId, 10),
        totalArea: parseFloat(it.totalArea),
        pricePerSqft: parseFloat(it.pricePerSqft),
        totalPrice: parseFloat(it.totalPrice),
        observation: it.observation || '',
        measurementUnit: it.measurementUnit || 'sqft',
      })),
    };

    try {
      setSaving(true);
      setError('');
      const saved = existing
        ? await updateChangeOrder(existing.id, payload)
        : await createChangeOrder(payload);
      onSaved?.(saved);
      onClose?.();
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.error || err.message || 'Failed to save change order');
    } finally {
      setSaving(false);
    }
  };

  const editingItem = editingIndex !== null ? items[editingIndex] : null;

  return (
    <>
      <Modal
        isOpen={isOpen}
        title={existing ? 'Edit Change Order' : 'New Change Order'}
        onConfirm={saving ? () => {} : handleSubmit}
        onCancel={onClose}
        disableBackdropClose
      >
        <FormWrapper>
          <KindToggle>
            <KindOption type="button" $active={!isCredit} onClick={() => setKind('Addition')}>
              <strong>Additional work</strong>
              <span>Bills the customer on its own invoice</span>
            </KindOption>
            <KindOption type="button" $active={isCredit} onClick={() => setKind('Credit')}>
              <strong>Credit / deduction</strong>
              <span>Work not performed or a discount; comes off the original invoice</span>
            </KindOption>
          </KindToggle>
          <Input
            label="Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={isCredit ? 'e.g. Crack filling not performed' : 'e.g. Additional patching on driveway entrance'}
            required
          />
          <TextArea
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
          <TextArea
            label="Reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
          {!isCredit && (
            <>
              <DiscountInput
                label="Discount (optional)"
                type={discountType}
                value={discountValue}
                onTypeChange={(type) => {
                  // 10 (%) and 10 ($) mean different things; start over
                  if (type !== discountType) { setDiscountType(type); setDiscountValue(''); }
                }}
                onValueChange={setDiscountValue}
                error={discountCalc.error}
              />
              {discountAmount > 0 && (
                <Input
                  label="Discount description (optional)"
                  value={discountReason}
                  onChange={(e) => setDiscountReason(e.target.value)}
                />
              )}
            </>
          )}
          <Checkbox
            label="Require customer signature"
            checked={requiresSignature}
            onChange={(e) => setRequiresSignature(e.target.checked)}
          />

          <div>
            <strong>{isCredit ? 'Credited items' : 'Items'}</strong>
            {isCredit && estimate?.items?.length > 0 && (
              <QuickPick>
                {estimate.items.map((estItem, i) => (
                  <button type="button" key={i} onClick={() => addOriginalService(estItem)}>
                    + {estItem.Service?.service_name || 'Service'} ({formatCurrency(estItem.totalPrice)})
                  </button>
                ))}
              </QuickPick>
            )}
            <ItemList>
              {items.map((item, index) => {
                const unit = getUnitInfo(item.measurementUnit).short;
                return (
                  <ItemRow key={index}>
                    <div className="item-info">
                      <div className="item-name">{item.serviceName || `Service #${item.serviceId}`}</div>
                      <div className="item-meta">
                        {item.totalArea} {unit} · {formatCurrency(item.pricePerSqft)}/{unit}
                      </div>
                    </div>
                    <strong>{formatCurrency(sign * item.totalPrice)}</strong>
                    <div className="item-actions">
                      <ActionButton type="button" onClick={() => openEditItem(index)} title="Edit">
                        <FaEdit />
                      </ActionButton>
                      <ActionButton type="button" variant="danger" onClick={() => handleItemRemove(index)} title="Remove">
                        <FaTrash />
                      </ActionButton>
                    </div>
                  </ItemRow>
                );
              })}
              <AddItemButton type="button" onClick={openAddItem}>
                <FaPlus /> {isCredit ? 'Add Custom Credit' : 'Add Item'}
              </AddItemButton>
            </ItemList>
          </div>

          {items.length > 0 && (
            <>
              {!isCredit && (
                <TotalsRow>
                  <span>Subtotal</span>
                  <span>{formatCurrency(subtotal)}</span>
                </TotalsRow>
              )}
              {discountAmount > 0 && (
                <TotalsRow>
                  <span>Discount{discountType === 'percentage' ? ` (${discountValue}%)` : ''}</span>
                  <span>- {formatCurrency(discountAmount)}</span>
                </TotalsRow>
              )}
              <TotalsRow>
                <span>{isCredit ? 'Total Credit' : 'Total'}</span>
                <span>{formatCurrency(sign * total)}</span>
              </TotalsRow>
            </>
          )}

          {error && (
            <div style={{ color: '#dc3545', fontSize: '0.9em' }}>{error}</div>
          )}
        </FormWrapper>
      </Modal>

      {serviceModalOpen && (
        <ServiceModal
          isOpen={serviceModalOpen}
          onClose={() => { setServiceModalOpen(false); setEditingIndex(null); }}
          onCancel={() => { setServiceModalOpen(false); setEditingIndex(null); }}
          onSave={handleItemSave}
          availableServices={services || []}
          existingService={editingItem}
        />
      )}
    </>
  );
};

export default ChangeOrderFormModal;
